import { componentQueryKeys } from 'src/query-keys'
import {
  IComponent,
  IComponentFilters,
  IComponentOverviewCounts,
  IMooringWithComponents,
  IProductWithComponents,
  IUpdateComponentBatchNumbers,
} from '../generated-types'
import { IProductCertification } from '../generated-types/product-certification'
import { queryClient } from '../query-client'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { generateQueryKey, useDelete, useGet, usePatch, usePost, usePut } from './reactQuery'

const baseUrl = (customerId: EntityId, positionId: EntityId) =>
  `customers/${customerId}/positions/${positionId}/components`
const componentOverviewBaseUrl = (customerId: EntityId) => `customers/${customerId}/components`

const baseAddBatchNumberUrl = (customerId: EntityId, positionId: EntityId) =>
  `customers/${customerId}/positions/${positionId}/components/AddBatchNumber`

const baseUpdateInlineValuesUrl = (customerId: EntityId, positionId: EntityId) =>
  `customers/${customerId}/positions/${positionId}/components/update-inline-values`

const baseBulkUpdateInstallationDateUrl = (customerId: EntityId, positionId: EntityId) =>
  `customers/${customerId}/positions/${positionId}/components/update-bulk-installationDate`

const updateBatchNumberBulkUrl = (customerId: EntityId) => `customers/${customerId}/update-batch-numbers-bulk`

export const componentsEndpoints = {
  getComponents(customerId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, positionId)}`
  },
  getComponent(customerId: EntityId, positionId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, positionId)}/${id}`
  },
  createComponent(customerId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, positionId)}`
  },
  updateComponent(customerId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, positionId)}`
  },
  updateComponentInlineValues(customerId: EntityId, positionId: EntityId) {
    return `${baseUpdateInlineValuesUrl(customerId, positionId)}`
  },
  updateInstallationDateBulk(customerId: EntityId, positionId: EntityId) {
    return `${baseBulkUpdateInstallationDateUrl(customerId, positionId)}`
  },
  addBatchNumber(customerId: EntityId, positionId: EntityId, id: EntityId) {
    return `${baseAddBatchNumberUrl(customerId, positionId)}/${id}`
  },
  updateBatchNumberBulk(customerId: EntityId) {
    return `${updateBatchNumberBulkUrl(customerId)}`
  },
  deleteComponent(customerId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, positionId)}`
  },
  deleteComponents(customerId: EntityId, positionId: EntityId) {
    return `${baseUrl(customerId, positionId)}/delete-components`
  },
  getMooringsWithComponents(customerId: EntityId) {
    return `${componentOverviewBaseUrl(customerId)}/moorings-with-components`
  },
  getProductsWithComponentsInMooring(customerId: EntityId) {
    return `${componentOverviewBaseUrl(customerId)}/products-with-components-by-mooring`
  },
  getComponentsOfProductInMooring(customerId: EntityId) {
    return `${componentOverviewBaseUrl(customerId)}/components-of-product-by-mooring`
  },
  getOverviewCounts(customerId: EntityId) {
    return `${componentOverviewBaseUrl(customerId)}/overview-counts`
  },
}

export const invalidateComponentsCache = {
  getComponents(customerId?: EntityId, positionId?: EntityId) {
    queryClient.invalidateQueries({
      queryKey: generateQueryKey('components', { customerId, ...(positionId != null && { positionId }) }),
      exact: false,
    })
  },
  getComponent(customerId: EntityId, positionId: EntityId, componentId?: EntityId) {
    queryClient.invalidateQueries(componentQueryKeys.GET_COMPONENTS(customerId, positionId, componentId))
  },
}

export const useGetComponents = (
  customerId: EntityId,
  positionId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  enabled = true,
) => {
  const params = { customerId, positionId, page, pageSize }
  return useGet<IPaginatedResult<IComponent>>(
    componentsEndpoints.getComponents(customerId, positionId),
    {
      page,
      pageSize,
    },
    { queryKey: generateQueryKey('components', params), enabled: !!positionId && enabled },
  )
}

export const useGetComponent = (customerId: EntityId, positionId: EntityId, id: EntityId) =>
  useGet<IComponent>(
    componentsEndpoints.getComponent(customerId, positionId, id),
    {},
    { queryKey: componentQueryKeys.GET_COMPONENTS(customerId, positionId, id) },
  )

export const useAddComponent = (customerId: EntityId, positionId: EntityId) =>
  usePost<IComponent, IComponent>(componentsEndpoints.createComponent(customerId, positionId))

export const useUpdateComponent = (customerId: EntityId, positionId: EntityId) =>
  usePut<IComponent, IComponent>(componentsEndpoints.updateComponent(customerId, positionId))

export const useAddBatchNumber = (customerId: EntityId, positionId: EntityId, id: EntityId) =>
  usePut<IProductCertification, IProductCertification>(componentsEndpoints.addBatchNumber(customerId, positionId, id))

export const useDeleteComponent = (customerId: EntityId, positionId: EntityId) =>
  useDelete<IComponent>(componentsEndpoints.deleteComponent(customerId, positionId))

export const useDeleteComponents = (customerId: EntityId, positionId: EntityId) =>
  usePatch<IComponent, { componentIds: number[] }>(componentsEndpoints.deleteComponents(customerId, positionId))

export const useUpdateComponentInlineValues = (customerId: EntityId, positionId: EntityId) =>
  usePut<IComponent, IComponent>(componentsEndpoints.updateComponentInlineValues(customerId, positionId))

export const useBulkUpdateComponentInstallationDate = (customerId: EntityId, positionId: EntityId) =>
  usePut<IComponent, { componentIds: number[]; installationDate: Date }>(
    componentsEndpoints.updateInstallationDateBulk(customerId, positionId),
  )

export const useBulkUpdateBatchNumber = (customerId: EntityId) =>
  usePut<IComponent, IUpdateComponentBatchNumbers>(componentsEndpoints.updateBatchNumberBulk(customerId))

export const useGetMooringsWithComponents = (customerId: EntityId, filters?: IComponentFilters) =>
  useGet<IMooringWithComponents[]>(componentsEndpoints.getMooringsWithComponents(customerId), { ...filters })

export const useGetProductsWithComponentsInMooring = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  filters: IComponentFilters,
) =>
  useGet<IProductWithComponents[]>(componentsEndpoints.getProductsWithComponentsInMooring(customerId), {
    localityId,
    mooringId,
    ...filters,
  })

export const useGetComponentsOfProductInMooring = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  productId: EntityId,
  filters: IComponentFilters,
) =>
  useGet<IComponent[]>(componentsEndpoints.getComponentsOfProductInMooring(customerId), {
    localityId,
    mooringId,
    productId,
    ...filters,
  })

export const useGetComponentsOverviewCounts = (customerId: EntityId, filters?: IComponentFilters) =>
  useGet<IComponentOverviewCounts>(componentsEndpoints.getOverviewCounts(customerId), { ...filters })

export const useDeleteComponentByPosition = (customerId: EntityId, positionId: EntityId) =>
  useDelete<IComponent>(componentsEndpoints.deleteComponent(customerId, positionId))
